import { ApplicationState } from 'app/types-store';
import { connect } from 'react-redux';
import { RiskMatrixProps } from 'app/components/risk-matrix';
import { transformToComponentFormat, preselectedRiskMatrixValues } from 'app/area/case/features/risk-matrix/risk-matrix-definition';
import { actionCreators } from 'app/area/case/draft/actions';
import get from 'lodash/get';
import { RiskMatrixTypeResult, PayloadRiskMatrixTypeResult } from 'app/area/case-report/risk-listing/types-store';
import { RiskMatrixDefinition } from 'app/area/case/types-store';

const getDefinitionFromStore = (state: ApplicationState, uiEngineProps: any) => {
  const hostProps = uiEngineProps && uiEngineProps.hostProps();
  const applicationId = hostProps.applicationId;
  return get(state, uiEngineProps.statePath).riskMatrixDefinitions[applicationId];
};

/**
 * Converting array of results to an object with results indexed by x and y coordinates for fast access.
 * The x and y are converted into the highcharts dataPoint coordinates similar to common axes on charts.
 * Point of x0y0 is the cell in the left bottom corner. It avoids the need of mapping through the axixValues
 * to find a correct option with the same value (it's done just once).
 *
 * @param result RiskMatrices array of results
 * @returns RiskMatrices object of results indexed by x and y
 */
export const convertRiskMatrixTypeResult = (
  result: PayloadRiskMatrixTypeResult,
  riskMatrixDefinition: RiskMatrixDefinition
): RiskMatrixTypeResult => {
  const cells = result?.cells?.reduce(
    (acc, cell) => {
      const xIndex = riskMatrixDefinition?.axes?.findIndex(a => a.id === 0);
      const yIndex = riskMatrixDefinition?.axes?.findIndex(a => a.id === 1);
      const x = riskMatrixDefinition?.axes?.[xIndex]?.data?.options.findIndex(option => option.value === cell.axisValues[xIndex]);
      const isMatrix1D = riskMatrixDefinition?.axes?.length === 1;
      const y = isMatrix1D ? 0 :
        riskMatrixDefinition?.axes?.[yIndex]?.data?.options.findIndex(option => option.value === cell.axisValues[yIndex]);
      if (x >= 0 && y >= 0) {
        acc['x' + x + 'y' + y] = cell;
      }
      return acc;
    }, {}
  ) || {};
  return { ...result, cells };
};

export const withRiskMatrix = (component: any, uiEngineProps: any) => {

  const stateToProps = (state: ApplicationState): Partial<RiskMatrixProps> => {
    const storeDefinition = getDefinitionFromStore(state, uiEngineProps);

    const definition = transformToComponentFormat(storeDefinition, uiEngineProps);
    return {
      riskMatrixDefinition: definition,
      preselectedValues: preselectedRiskMatrixValues(get(state, uiEngineProps.statePath), uiEngineProps, definition)
    };
  };

  const dispatchToProps = (dispatch: any): Partial<RiskMatrixProps> => {
    const dataPath = uiEngineProps.resolveContext.path;
    const changeAxisValue = (v: number, axisCodetableId: number) => {
      const immediateSave = uiEngineProps.componentDescriptor.properties
        && uiEngineProps.componentDescriptor.properties.immediateSaveOnValueChange;
      const axisComponent = uiEngineProps.componentDescriptor.children.find(c => c.codetable === axisCodetableId);
      dispatch(actionCreators.valueChanged(dataPath, axisComponent.dataTag, [{ value: v }], !!immediateSave));
      return;
    };

    return {
      onSelectedXChanged: changeAxisValue,
      onSelectedYChanged: changeAxisValue
    };
  };

  return connect<any, any, any>(stateToProps, dispatchToProps)(component);
};
